/**
 * DemoRibbon — variant legibility for non-engineers (deploy-hardening DH1.2).
 *
 * A small persistent DEMO badge rendered ONLY in demo-mode artifacts
 * (`import.meta.env.VITE_APP_ENV === 'demo'`), so anyone looking at a deployed
 * URL can tell the sample-data demo apart from the real-API build without
 * counting nav modules. The env check is a static string comparison that Vite
 * bakes in at build time, so Rollup dead-code-eliminates this component out of
 * `build:api` artifacts entirely (App.tsx mounts it behind the same static
 * check, which also drops the import).
 *
 * Informational, not alarming: warning-tone bg/border tokens carry the tone
 * (never colorPrimary) while the label uses neutral colorText — light-theme
 * colorWarningText on colorWarningBg is only 1.83:1 (#faad14 on #fffbe6),
 * far below the WCAG 4.5:1 gate, and the word "Demo" carries the meaning so
 * no colour-only signal is lost. Pointer-events none so it can never obstruct
 * or intercept content, zIndex below AntD modals (1000), and bottom-LEFT so
 * it cannot overlap the DevFlagPanel FAB (bottom-right, zIndex 1100).
 */

import { theme } from 'antd';
import { useTranslation } from 'react-i18next';

const DemoRibbon = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  // Static comparison — constant-folded per build mode (defense-in-depth with
  // the identical gate at the App.tsx mount point).
  if (import.meta.env.VITE_APP_ENV !== 'demo') return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        left: 16,
        bottom: 16,
        // Below AntD modals/drawers (1000) and the DevFlagPanel FAB (1100).
        zIndex: 900,
        pointerEvents: 'none',
        padding: `${token.paddingXXS}px ${token.paddingSM}px`,
        backgroundColor: token.colorWarningBg,
        border: `1px solid ${token.colorWarningBorder}`,
        borderRadius: token.borderRadiusSM,
        color: token.colorText,
        fontSize: token.fontSizeSM,
        fontWeight: 500,
        lineHeight: 1.6,
        boxShadow: token.boxShadowTertiary,
      }}
    >
      {t('demoRibbon.label')}
    </div>
  );
};

export default DemoRibbon;
